
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { PageHeader } from "@/components/admin/payments/PageHeader";
import { FiltersSection } from "@/components/admin/payments/FiltersSection";
import { PaymentStatCards } from "@/components/admin/payments/PaymentStatCards";
import { ChartSection } from "@/components/admin/payments/ChartSection";
import { TableSection } from "@/components/admin/payments/TableSection";
import { useProfile } from "@/hooks/useProfile";
import { PaymentCategoriesSection } from "@/components/admin/payments/PaymentCategoriesSection";
import { PendingReceiptsManager } from "@/components/admin/payments/PendingReceiptsManager";
import { ArticlesSection } from "@/components/admin/payments/articles/ArticlesSection";
import { ArticleSaleDialog } from "@/components/admin/payments/articles/ArticleSaleDialog";
import { RegisterPaymentDialog } from "@/components/admin/payments/RegisterPaymentDialog";
import { useAuth } from "@/contexts/AuthContext";
import { useRecepcionistaRedirect } from "@/hooks/useRecepcionistaRedirect";
import { getPaymentDate } from "@/lib/paymentDate";
import { DebtorsPanel } from "@/components/payments/DebtorsPanel";
import type { DateRange } from "react-day-picker";

const TotalPagos = () => {
  const [searchParams] = useSearchParams();
  const paramGymId = searchParams.get("gym_id");
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { user } = useAuth();
  const { isRecepcionista } = useRecepcionistaRedirect();

  // Fallback: query gym_managers if profile.gym_id is null
  const { data: managedGymId } = useQuery({
    queryKey: ["managedGymId", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gym_managers")
        .select("gym_id")
        .eq("manager_id", user!.id)
        .limit(1)
        .single();
      if (error) return null;
      return data?.gym_id || null;
    },
    enabled: !!user && !paramGymId && !profile?.gym_id,
  });

  const gymId = paramGymId || profile?.gym_id || managedGymId || null;

  const [totalAmount, setTotalAmount] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState<string>("all_months");
  const [selectedYear, setSelectedYear] = useState<string>("all_years");
  const [selectedDay, setSelectedDay] = useState<string>("all_days");
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);

  const { data: payments, isLoading } = useQuery({
    queryKey: ["payments", gymId],
    queryFn: async () => {
      if (!gymId) return [];

      // Step 1: Get all user IDs belonging to this gym
      const [profilesRes, pendingRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, username, first_name, last_name, gym_id")
          .eq("gym_id", gymId),
        supabase
          .from("pending_members")
          .select("id, first_name, last_name, gym_id, dni")
          .eq("gym_id", gymId),
      ]);

      const profilesList = profilesRes.data || [];
      const pendingList = (pendingRes.data || []).filter(
        (p: any) => p.dni !== "__WALKIN__"
      );
      const walkinIds = new Set(
        (pendingRes.data || [])
          .filter((p: any) => p.dni === "__WALKIN__")
          .map((p: any) => p.id)
      );

      const allUserIds = [
        ...profilesList.map(p => p.id),
        ...pendingList.map(p => p.id),
        ...Array.from(walkinIds) as string[],
      ];

      if (allUserIds.length === 0) return [];

      // Step 2: Fetch payments in batches to bypass 1000-row limit
      const batchSize = 500;
      const allPayments: any[] = [];

      // Split userIds into chunks for the .in() filter
      for (let i = 0; i < allUserIds.length; i += batchSize) {
        const userIdBatch = allUserIds.slice(i, i + batchSize);
        
        // For each batch of user IDs, paginate payments
        let from = 0;
        const pageSize = 1000;
        let hasMore = true;

        while (hasMore) {
          const { data, error } = await supabase
            .from("payments")
            .select("*")
            .in("user_id", userIdBatch)
            .order("paid_at", { ascending: false, nullsFirst: false })
            .range(from, from + pageSize - 1);

          if (error) throw error;
          if (data && data.length > 0) {
            allPayments.push(...data);
            from += pageSize;
            hasMore = data.length === pageSize;
          } else {
            hasMore = false;
          }
        }
      }

      // Step 3: Map profile info onto payments
      const profilesMap = new Map(profilesList.map(p => [p.id, p]));
      const pendingMap = new Map(pendingList.map(p => [p.id, p]));

      return allPayments.map(payment => {
        const prof = profilesMap.get(payment.user_id);
        const pending = pendingMap.get(payment.user_id);
        const isWalkin = walkinIds.has(payment.user_id);

        return {
          ...payment,
          profiles: isWalkin
            ? null
            : prof || (pending ? {
                username: null,
                first_name: pending.first_name,
                last_name: pending.last_name,
                gym_id: pending.gym_id,
              } : null),
        };
      });
    },
    enabled: !!gymId,
  });

  const { data: gym } = useQuery({
    queryKey: ["gym", gymId],
    queryFn: async () => {
      if (!gymId) return null;
      const { data, error } = await supabase
        .from("gyms")
        .select("name")
        .eq("id", gymId)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!gymId,
  });

  const filteredPayments = payments?.filter((payment) => {
    const paymentDate = getPaymentDate(payment);
    const month = format(paymentDate, "MMMM", { locale: es });
    const year = paymentDate.getFullYear().toString();
    const day = paymentDate.getDate().toString();

    const monthMatch = selectedMonth === "all_months" || month === selectedMonth;
    const yearMatch = selectedYear === "all_years" || year === selectedYear;
    const dayMatch = selectedDay === "all_days" || day === selectedDay;

    let rangeMatch = true;
    if (dateRange?.from) {
      const from = new Date(dateRange.from);
      from.setHours(0, 0, 0, 0);
      const to = new Date(dateRange.to ?? dateRange.from);
      to.setHours(23, 59, 59, 999);
      rangeMatch = paymentDate >= from && paymentDate <= to;
    }

    return monthMatch && yearMatch && dayMatch && rangeMatch;
  });

  const averageAmount = filteredPayments?.length 
    ? totalAmount / filteredPayments.length 
    : 0;
  
  const maxAmount = filteredPayments?.length 
    ? Math.max(...filteredPayments.map(p => p.amount)) 
    : 0;

  const handleClearFilters = () => {
    setSelectedMonth("all_months");
    setSelectedYear("all_years");
    setSelectedDay("all_days");
    setDateRange(undefined);
  };

  useEffect(() => {
    if (filteredPayments) {
      const total = filteredPayments.reduce((acc, payment) => acc + payment.amount, 0);
      setTotalAmount(total);
    }
  }, [filteredPayments]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/10 via-background to-background text-primary pt-16 px-3 sm:px-4 lg:px-6 pb-6">
      <div className="max-w-7xl mx-auto">
        <PageHeader gymId={gymId} gymName={gym?.name} />

        <div className="space-y-6">
          {gymId && (
            <div className="flex flex-wrap gap-3 animate-fade-in">
              <PaymentCategoriesSection gymId={gymId} />
              <ArticlesSection gymId={gymId} />
              <ArticleSaleDialog gymId={gymId} />
              <RegisterPaymentDialog gymId={gymId} />
              <Button
                onClick={() => navigate(`/arqueo-caja?gym_id=${gymId}`)}
                className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-600/90 hover:to-emerald-500/90 text-white"
              >
                <Calculator className="mr-2 h-4 w-4" />
                Arqueo de Caja
              </Button>
            </div>
          )}

          <div className="animate-fade-in">
            <PendingReceiptsManager gymId={gymId} />
          </div>

          {gymId && gym?.name?.toUpperCase().includes("MASTERGYM") && (
            <div className="animate-fade-in">
              <DebtorsPanel gymId={gymId} />
            </div>
          )}
          {isRecepcionista === false && (
            <div className="animate-fade-in stagger-1">
              <FiltersSection
                selectedMonth={selectedMonth}
                setSelectedMonth={setSelectedMonth}
                selectedYear={selectedYear}
                setSelectedYear={setSelectedYear}
                selectedDay={selectedDay}
                setSelectedDay={setSelectedDay}
                dateRange={dateRange}
                setDateRange={setDateRange}
                payments={payments}
                onClearFilters={handleClearFilters}
              />
            </div>
          )}

          {isRecepcionista === false && (
            <div className="animate-fade-in stagger-2">
              <PaymentStatCards 
                totalAmount={totalAmount} 
                paymentsCount={filteredPayments?.length || 0}
                averageAmount={averageAmount}
                maxAmount={maxAmount}
              />
            </div>
          )}

          {isRecepcionista === false && (
            <div className="animate-fade-in stagger-3">
              <ChartSection payments={filteredPayments} />
            </div>
          )}

          <div className="animate-fade-in stagger-4">
            <TableSection 
              payments={filteredPayments} 
              isLoading={isLoading} 
              gymId={gymId} 
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TotalPagos;
